import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COR_BARRA_QTD = '#2f9cf5';

type DadoRanking = {
  codigoItem: string;
  nomeItem: string;
  quantidadeVendida: number;
  rotuloRanking: string;
  valorTotal?: number;
};

type CustomRankingChartProps = {
  dados: DadoRanking[];
  altura?: number;
  barrasVerticais?: boolean;
  tituloQuantidade?: string;
  rotuloValorExtra?: string;
  formatarValorExtra?: (valor: number) => string;
};

type EntradaTooltip = {
  payload?: DadoRanking;
};

function CustomTooltipRanking({
  active,
  payload,
  tituloQuantidade = 'Qtd. Vendida',
  rotuloValorExtra,
  formatarValorExtra,
}: {
  active?: boolean;
  payload?: EntradaTooltip[];
  tituloQuantidade?: string;
  rotuloValorExtra?: string;
  formatarValorExtra?: (valor: number) => string;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  if (!item) return null;

  const titulo = item.codigoItem ? `${item.codigoItem} — ${item.nomeItem}` : item.nomeItem;

  return (
    <div
      style={{
        borderRadius: 14,
        border: '1px solid #d5e2eb',
        backgroundColor: '#f8fbfe',
        boxShadow: '0 14px 32px rgba(15, 53, 80, 0.12)',
        padding: '10px 16px',
        fontSize: 13,
        color: '#3a4a57',
        minWidth: 200,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#1e2e3a' }}>
        {titulo}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor: COR_BARRA_QTD,
          }}
        />
        <span>{tituloQuantidade}:</span>
        <strong>{item.quantidadeVendida}</strong>
      </div>
      {rotuloValorExtra && item.valorTotal !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <span
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: '#20c997',
            }}
          />
          <span>{rotuloValorExtra}:</span>
          <strong>
            {formatarValorExtra ? formatarValorExtra(item.valorTotal) : item.valorTotal}
          </strong>
        </div>
      )}
    </div>
  );
}

function CustomRankingChart({
  dados,
  altura,
  barrasVerticais = false,
  tituloQuantidade = 'Qtd. Vendida',
  rotuloValorExtra,
  formatarValorExtra,
}: CustomRankingChartProps) {
  const alturaCalculada = altura || 200;
  const modoCompacto = dados.length >= 10;
  const modoMuitoCompacto = dados.length >= 14;

  if (barrasVerticais) {
    return (
      <ResponsiveContainer width='100%' height={alturaCalculada}>
        <BarChart
          data={dados}
          margin={{ top: 16, right: 8, left: 0, bottom: 48 }}
          barCategoryGap='16%'
        >
          <defs>
            <linearGradient id='rankingGradientQtdV' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={COR_BARRA_QTD} stopOpacity={0.95} />
              <stop offset='100%' stopColor={COR_BARRA_QTD} stopOpacity={0.55} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke='#d5dce3' strokeDasharray='0' vertical={false} />

          <XAxis
            type='category'
            dataKey='rotuloRanking'
            tick={{ fill: '#6a727a', fontSize: 9 }}
            axisLine={false}
            tickLine={false}
            angle={-35}
            textAnchor='end'
            interval={0}
            tickFormatter={(v: string) => (v.length > 18 ? v.substring(0, 16) + '…' : v)}
          />

          <YAxis
            type='number'
            allowDecimals={false}
            width={28}
            tick={{ fill: '#6a727a', fontSize: 9 }}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip
            cursor={{ fill: 'rgba(47, 156, 245, 0.06)' }}
            content={(
              <CustomTooltipRanking
                tituloQuantidade={tituloQuantidade}
                rotuloValorExtra={rotuloValorExtra}
                formatarValorExtra={formatarValorExtra}
              />
            )}
          />

          <Bar
            dataKey='quantidadeVendida'
            name={tituloQuantidade}
            fill='url(#rankingGradientQtdV)'
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          >
            <LabelList
              dataKey='quantidadeVendida'
              position='insideTop'
              formatter={(v) => (Number(v) > 0 ? Number(v) : '')}
              style={{ fill: '#ffffff', fontSize: 9, fontWeight: 700 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={alturaCalculada}>
      <BarChart
        data={dados}
        layout='vertical'
          margin={{ top: modoCompacto ? 8 : 12, right: 42, left: 0, bottom: modoCompacto ? 0 : 6 }}
          barCategoryGap={modoMuitoCompacto ? '12%' : modoCompacto ? '10%' : '16%'}
      >
        <defs>
          <linearGradient id='rankingGradientQtd' x1='1' y1='0' x2='0' y2='0'>
            <stop offset='0%' stopColor={COR_BARRA_QTD} stopOpacity={0.95} />
            <stop offset='100%' stopColor={COR_BARRA_QTD} stopOpacity={0.55} />
          </linearGradient>
        </defs>

        <CartesianGrid stroke='#d5dce3' strokeDasharray='0' horizontal={false} />

        <XAxis
          type='number'
          allowDecimals={false}
          tick={{ fill: '#6a727a', fontSize: 9 }}
          axisLine={false}
          tickLine={false}
        />

        <YAxis
          type='category'
          dataKey='rotuloRanking'
          width={modoMuitoCompacto ? 108 : modoCompacto ? 120 : 132}
          tick={{ fill: '#6a727a', fontSize: modoMuitoCompacto ? 7 : modoCompacto ? 8 : 9 }}
          axisLine={false}
          tickLine={false}
          interval={0}
          tickFormatter={(v: string) => {
            const limite = modoMuitoCompacto ? 14 : modoCompacto ? 18 : 22;
            return v.length > limite ? v.substring(0, limite - 2) + '…' : v;
          }}
        />

        <Tooltip
          cursor={{ fill: 'rgba(47, 156, 245, 0.06)' }}
          content={(
            <CustomTooltipRanking
              tituloQuantidade={tituloQuantidade}
              rotuloValorExtra={rotuloValorExtra}
              formatarValorExtra={formatarValorExtra}
            />
          )}
        />

        <Bar
          dataKey='quantidadeVendida'
          name={tituloQuantidade}
          fill='url(#rankingGradientQtd)'
          radius={[0, 3, 3, 0]}
          maxBarSize={modoMuitoCompacto ? 10 : modoCompacto ? 14 : 18}
        >
          <LabelList
            dataKey='quantidadeVendida'
            position='right'
            formatter={(v) => (Number(v) > 0 ? Number(v) : '')}
            style={{ fill: COR_BARRA_QTD, fontSize: modoMuitoCompacto ? 7 : modoCompacto ? 8 : 9, fontWeight: 700 }}
          />

        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export type { DadoRanking };
export default CustomRankingChart;